import * as React from 'react'
import {useBackupToolReport, getBackupToolReport, type BackupToolReportModel} from './api/get-backup-tool-report'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {DataTable} from '@/components/DataTable'
import {ColumnDef, SortingState} from '@tanstack/react-table'
import {Link} from 'react-router-dom'
import {Building2, Download, AlertCircle, AlertTriangle} from 'lucide-react'
import {Badge} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import moment from 'moment'

const CRITICAL_THRESHOLD = 50
const WARNING_THRESHOLD = 10

const columns: ColumnDef<BackupToolReportModel>[] = [
    {
        accessorKey: 'accountName',
        header: 'Account',
        cell: ({row}) => {
            const account = row.original
            return (
                <Link
                    to={`/accounts/${account.accountId}`}
                    className='text-primary hover:underline flex items-center gap-1'
                >
                    <Building2 className='h-4 w-4'/>
                    {account.accountName}
                </Link>
            )
        },
    },
    {
        accessorKey: 'activeStores',
        header: 'Stores',
        cell: ({row}) => <span>{row.getValue('activeStores')}</span>,
    },
    {
        accessorKey: 'hasFastboundUsers',
        header: 'FB',
        cell: ({row}) => {
            const hasFB = row.getValue('hasFastboundUsers') as boolean
            return <Badge variant={hasFB ? 'default' : 'secondary'}>{hasFB ? 'Yes' : 'No'}</Badge>
        },
    },
    {
        accessorKey: 'hasInstalledBackupTool',
        id: 'backUpToolClientId',
        header: 'Tool Installed',
        cell: ({row}) => {
            const hasInstalled = row.getValue('backUpToolClientId') as boolean
            return <Badge variant={hasInstalled ? 'default' : 'destructive'}>{hasInstalled ? 'Yes' : 'No'}</Badge>
        },
    },
    {
        accessorKey: 'totalAttachments',
        header: 'Total',
        cell: ({row}) => <span className='font-medium'>{row.getValue('totalAttachments')}</span>,
    },
    {
        accessorKey: 'attachmentsNeedingBackup',
        header: 'Need Backup',
        cell: ({row}) => {
            const needingBackup = row.getValue('attachmentsNeedingBackup') as number
            const percent = row.getValue('percentNeedingBackup') as number
            return (
                <div className='flex items-center gap-1'>
                    { percent > CRITICAL_THRESHOLD ? <AlertCircle className='h-4 w-4 text-red-500'/> : percent > WARNING_THRESHOLD ? <AlertTriangle className='h-4 w-4 text-orange-500'/>  : <></>}
                    <span className={`font-bold ${percent > CRITICAL_THRESHOLD ? 'text-destructive' : percent > WARNING_THRESHOLD ? 'text-orange-500' : 'text-success'}`}>{needingBackup}</span>
                </div>
            )
        },
    },
    {
        accessorKey: 'attachmentsBackedUp',
        header: 'Backed Up',
        cell: ({row}) => {
            const percent = row.getValue('percentNeedingBackup') as number
            return (
                <div className='flex items-center gap-1'>
                    <span className={percent > CRITICAL_THRESHOLD ? 'text-destructive' : percent > WARNING_THRESHOLD ? 'text-orange-500' : 'text-success'}>{row.getValue('attachmentsBackedUp')}</span>
                </div>
            );
        },
    },
    {
        accessorKey: 'percentNeedingBackup',
        id: 'percentNeedingBackup',
        header: '% Need Backup',
        cell: ({row}) => {
            const percent = row.getValue('percentNeedingBackup') as number
            return (
                <Badge variant={percent > CRITICAL_THRESHOLD ? 'destructive' : percent > WARNING_THRESHOLD ? 'warning' : 'success'}>
                    {percent.toFixed(1)}%
                </Badge>
            )
        },
    },
    {
        accessorKey: 'lastBackupDate',
        id: 'lastBackupDate',
        header: 'Last Backup',
        cell: ({row}) => {
            const date = row.getValue('lastBackupDate') as string | null
            const timeSince = row.original.timeSinceLastBackup
            return (
                <div className='flex flex-col'>
                    <span className='text-xs text-muted-foreground'>{date ? moment(date).format('MM/DD/YY') : '—'}</span>
                    <span className='text-xs font-medium'>{timeSince}</span>
                </div>
            )
        },
    },
    {
        accessorKey: 'ownerEmails',
        header: 'Owner Emails',
        cell: ({row}) => {
            const emails = row.getValue('ownerEmails') as string | null
            return <span className='text-xs truncate max-w-[200px] block'>{emails || '—'}</span>
        },
        enableSorting: false,
    },
]

export default function BackupToolReport() {
    const [pageIndex, setPageIndex] = React.useState(0)
    const [pageSize, setPageSize] = React.useState(25)
    const [sorting, setSorting] = React.useState<SortingState>([
        {id: 'attachmentsNeedingBackup', desc: true},
    ])
    const [accountNameFilter, setAccountNameFilter] = React.useState('')
    const [hasFastboundFilter, setHasFastboundFilter] = React.useState<string>('all')
    const [hasBackupToolFilter, setHasBackupToolFilter] = React.useState<string>('all')
    const [minDaysFilter, setMinDaysFilter] = React.useState<string>('')
    const [minAttachmentsFilter, setMinAttachmentsFilter] = React.useState<string>('')
    const [isExporting, setIsExporting] = React.useState(false)

    const filter = React.useMemo(
        () => ({
            startIndex: pageIndex * pageSize,
            limit: pageSize,
            sort: sorting[0]?.id || 'attachmentsNeedingBackup',
            sortOrder: sorting[0]?.desc ? ('desc' as const) : ('asc' as const),
            accountName: accountNameFilter || undefined,
            hasFastboundUsers: hasFastboundFilter === 'all' ? undefined : hasFastboundFilter === 'yes',
            hasInstalledBackupTool: hasBackupToolFilter === 'all' ? undefined : hasBackupToolFilter === 'yes',
            minDaysSinceLastBackup: minDaysFilter ? Number(minDaysFilter) : undefined,
            minAttachmentsNeedingBackup: minAttachmentsFilter ? Number(minAttachmentsFilter) : undefined,
        }),
        [pageIndex, pageSize, sorting, accountNameFilter, hasFastboundFilter, hasBackupToolFilter, minDaysFilter, minAttachmentsFilter]
    )
    const {data, isLoading, isFetching} = useBackupToolReport({filter})

    const handleExport = async () => {
        if (!data?.totalCount) return
        
        setIsExporting(true)
        try {
            // Fetch all data without pagination
            const allDataFilter = {
                ...filter,
                startIndex: 0,
                limit: data.totalCount, // Fetch all records
            }
            
            const allData = await getBackupToolReport(allDataFilter)

            const csvRows = [
                [
                    'Account Name',
                    'Stores',
                    'Has Fastbound',
                    'Tool Installed',
                    'Total Attachments',
                    'Need Backup',
                    'Backed Up',
                    '% Need Backup',
                    'Last Backup Date',
                    'Days Since Backup',
                    'Owner Emails',
                    'Owner Phones',
                ].join(','),
                ...allData.backupToolReports.map(account => [
                `"${account.accountName.replace(/"/g, '""')}"`,
                account.activeStores,
                account.hasFastboundUsers ? 'Yes' : 'No',
                account.hasInstalledBackupTool ? 'Yes' : 'No',
                account.totalAttachments,
                account.attachmentsNeedingBackup,
                account.attachmentsBackedUp,
                account.percentNeedingBackup.toFixed(2),
                account.lastBackupDate ? moment(account.lastBackupDate).format('MM/DD/YYYY') : '',
                account.daysSinceLastBackup ?? '',
                `"${(account.ownerEmails || '').replace(/"/g, '""')}"`,
                    `"${(account.ownerPhoneNumbers || '').replace(/"/g, '""')}"`,
                ].join(','))
            ]

            const csvContent = csvRows.join('\n')
            const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'})
            const link = document.createElement('a')
            const url = URL.createObjectURL(blob)
            link.setAttribute('href', url)
            link.setAttribute('download', `backup_tool_report_${new Date().toISOString().split('T')[0]}.csv`)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } catch (error) {
            console.error('Error exporting data:', error)
        } finally {
            setIsExporting(false)
        }
    }

    return (
        <div className='space-y-4'>
            <Card>
                <CardHeader>
                    <div className='flex items-center justify-between'>
                        <div>
                            <CardTitle>Backup Tool Report</CardTitle>
                            <p className='text-sm text-muted-foreground mt-1'>
                                Accounts with attachments needing backup and their backup status
                            </p>
                        </div>
                        <Button
                            onClick={handleExport}
                            variant='outline'
                            disabled={isLoading || isExporting || !data?.totalCount}
                        >
                            <Download className='h-4 w-4 mr-2'/>
                            {isExporting ? `Exporting ${data?.totalCount || 0} records...` : 'Export CSV'}
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className='space-y-4'>
                        {/* Filters */}
                        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4'>
                            <div className='space-y-2'>
                                <Label htmlFor='account-name'>Account Name</Label>
                                <Input
                                    id='account-name'
                                    placeholder='Search accounts...'
                                    value={accountNameFilter}
                                    onChange={(e) => {
                                        setAccountNameFilter(e.target.value)
                                        setPageIndex(0)
                                    }}
                                />
                            </div>

                            <div className='space-y-2'>
                                <Label htmlFor='has-fastbound'>Has Fastbound</Label>
                                <Select
                                    value={hasFastboundFilter}
                                    onValueChange={(value) => {
                                        setHasFastboundFilter(value)
                                        setPageIndex(0)
                                    }}
                                >
                                    <SelectTrigger id='has-fastbound'>
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value='all'>All</SelectItem>
                                        <SelectItem value='yes'>Yes</SelectItem>
                                        <SelectItem value='no'>No</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className='space-y-2'>
                                <Label htmlFor='has-backup-tool'>Tool Installed</Label>
                                <Select
                                    value={hasBackupToolFilter}
                                    onValueChange={(value) => {
                                        setHasBackupToolFilter(value)
                                        setPageIndex(0)
                                    }}
                                >
                                    <SelectTrigger id='has-backup-tool'>
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value='all'>All</SelectItem>
                                        <SelectItem value='yes'>Yes</SelectItem>
                                        <SelectItem value='no'>No</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className='space-y-2'>
                                <Label htmlFor='min-days'>Min Days Since Backup</Label>
                                <Input
                                    id='min-days'
                                    type='number'
                                    placeholder='e.g., 3'
                                    value={minDaysFilter}
                                    onChange={(e) => {
                                        setMinDaysFilter(e.target.value)
                                        setPageIndex(0)
                                    }}
                                />
                            </div>

                            <div className='space-y-2'>
                                <Label htmlFor='min-attachments'>Min Attachments Needed</Label>
                                <Input
                                    id='min-attachments'
                                    type='number'
                                    placeholder='e.g., 1'
                                    value={minAttachmentsFilter}
                                    onChange={(e) => {
                                        setMinAttachmentsFilter(e.target.value)
                                        setPageIndex(0)
                                    }}
                                />
                            </div>
                        </div>
                        <>
                            <>

                                <div className='mb-4 flex items-center justify-between'>
                                    <div className='text-sm text-muted-foreground'>
                                        {data != null ? (
                                                <>
                                                    Found {data?.totalCount} account{data?.totalCount !== 1 ? 's' : ''}
                                                </>
                                            )
                                            : (
                                                <>loading accounts</>
                                            )}

                                    </div>
                                </div>
                                <DataTable
                                    columns={columns}
                                    data={data?.backupToolReports || []}
                                    isLoading={isFetching}
                                    loadingMessage={isLoading ? 'Loading accounts...' : 'Updating...'}
                                    enablePagination={true}
                                    serverPagination={true}
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    pageCount={Math.ceil((data?.totalCount || 0) / pageSize)}
                                    onPaginationChange={(updater) => {
                                        const newState =
                                            typeof updater === 'function' ? updater({pageIndex, pageSize}) : updater
                                        setPageIndex(newState.pageIndex)
                                        setPageSize(newState.pageSize)
                                    }}
                                    sorting={sorting}
                                    onSortingChange={setSorting}
                                    serverSorting={true}
                                    scrollContainerClassName='max-h-[calc(100vh-500px)] overflow-y-auto'
                                />
                            </>
                        </>
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}

